<script setup lang="ts">
import { nextTick, onMounted, useTemplateRef } from 'vue'
import DreamaLoginLogo from '@/components/dreama-login-logo'
import { useUserStore, type UserInfo } from '@/stores/user'
import { loginWithBlankClose } from '@/utils/login'
import { stateImages } from '@/utils/state-images'
import type { Props } from './type'

defineOptions({
  name: 'LoginPrompt',
})

withDefaults(defineProps<Props>(), {
  message: '登录后查看更多内容',
  buttonLabel: '登录造梦次元',
})

const userStore = useUserStore()
const buttonRef = useTemplateRef<HTMLButtonElement>('buttonRef')

async function handleLogin() {
  const userInfo = await loginWithBlankClose({
    logo: DreamaLoginLogo,
  })

  if (userInfo) {
    userStore.setUserInfo(userInfo as unknown as UserInfo)
  }
}

onMounted(() => {
  void nextTick(() => buttonRef.value?.focus())
})
</script>

<template>
  <section class="login-prompt" aria-label="登录引导">
    <p class="login-prompt__message">{{ message }}</p>
    <button ref="buttonRef" type="button" class="login-prompt__button" @click="handleLogin">
      <img
        class="login-prompt__button-image"
        :src="stateImages.loginPromptWave"
        alt=""
        loading="eager"
        decoding="async"
        aria-hidden="true"
      />
      <span class="login-prompt__button-label">{{ buttonLabel }}</span>
    </button>
  </section>
</template>

<style scoped>
.login-prompt {
  display: flex;
  min-height: 240px;
  min-width: 0;
  width: 100%;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 32px 16px;
  color: var(--app-text-main);
  text-align: center;
}

.login-prompt__message {
  max-width: min(100%, 360px);
  margin: 0;
  color: var(--app-text-main);
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
}

.login-prompt__button {
  display: inline-flex;
  min-height: 40px;
  max-width: 100%;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 24px 0 8px;
  border: 0;
  border-radius: 12px;
  appearance: none;
  background: var(--distribute-accent);
  color: var(--app-positive-foreground);
  cursor: pointer;
  font: inherit;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  outline: none;
  overflow: hidden;
  transition:
    background-color var(--app-interaction-duration) var(--app-interaction-ease),
    transform var(--app-interaction-duration) var(--app-interaction-ease);
}

.login-prompt__button-image {
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
  object-fit: cover;
  pointer-events: none;
}

.login-prompt__button-label {
  min-width: 0;
  overflow-wrap: anywhere;
}

.login-prompt__button:hover,
.login-prompt__button:focus-visible {
  background: var(--app-positive);
}

.login-prompt__button:focus-visible {
  box-shadow:
    0 0 0 3px var(--app-bg),
    0 0 0 5px var(--distribute-accent);
}

.login-prompt__button:active {
  transform: scale(var(--app-interaction-standard-active-scale));
}

@media (max-width: 375px) {
  .login-prompt {
    padding: 28px 12px;
  }

  .login-prompt__message {
    font-size: 15px;
    line-height: 21px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .login-prompt__button {
    transition: none;
  }
}
</style>
